import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '../../app/appLayout.js';
import { transactionsApi } from '../../../api/transactionsApi.js';

const ReviewUpload = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategoriesMap, setSubCategoriesMap] = useState({});
    const [editedRows, setEditedRows] = useState(new Set());
    const [submittingIndex, setSubmittingIndex] = useState(null);
    const [submitErrorByRow, setSubmitErrorByRow] = useState({});

    // Get data from location state
    useEffect(() => {
        if (location.state?.data?.results?.rows) {
            const initialRows = location.state.data.results.rows.map((row, index) => ({
                ...row,
                description: row.description ?? row.rawDesc ?? '',
                originalIndex: index
            }));
            setRows(initialRows);
        } else {
            // If no data, redirect back to upload
            navigate('/upload');
        }
    }, [location.state, navigate]);

    // Fetch categories, then subcategories for each, so backend rows (e.g. category FOOD, subCategory EAT OUT) show correctly
    useEffect(() => {
        const loadCategoriesAndSubCategories = async () => {
            try {
                const res = await transactionsApi.getCategories();
                if (res?.msg === "Success" && res?.data?.rows) {
                    const catList = res.data.rows;
                    setCategories(catList);
                    const map = {};
                    await Promise.all(catList.map(async (category) => {
                        const subRes = await transactionsApi.getSubCategories(category);
                        if (subRes?.msg === "Success" && subRes?.data?.rows) {
                            map[category] = subRes.data.rows;
                        }
                    }));
                    setSubCategoriesMap(map);
                }
            } catch (err) {
                console.error('Failed to fetch categories/subcategories:', err);
            }
        };
        loadCategoriesAndSubCategories();
    }, []);

    // Fetch subcategories when category changes
    const fetchSubCategories = async (category) => {
        if (!category || category === '') {
            return [];
        }
        try {
            const res = await transactionsApi.getSubCategories(category);
            if (res?.msg === "Success" && res?.data?.rows) {
                return res.data.rows;
            }
        } catch (err) {
            console.error('Failed to fetch subcategories:', err);
        }
        return [];
    };

    const handleFieldChange = async (rowIndex, fieldName, value) => {
        // If category changes, fetch subcategories first
        if (fieldName === 'category') {
            const subCats = await fetchSubCategories(value);
            setSubCategoriesMap(prev => ({
                ...prev,
                [value]: subCats
            }));
        }

        setRows(prevRows => {
            const newRows = [...prevRows];
            const row = { ...newRows[rowIndex] };
            
            // If category changes, reset subCategory
            if (fieldName === 'category') {
                row.subCategory = '';
            }
            
            row[fieldName] = value;
            newRows[rowIndex] = row;
            return newRows;
        });

        // Mark this row as edited
        setEditedRows(prev => new Set(prev).add(rowIndex));
    };

    const handleSubmit = async (rowIndex) => {
        const row = rows[rowIndex];
        const isReview = row.status === 'REVIEW';
        const isInvalidEdited = row.status === 'INVALID' && editedRows.has(rowIndex);
        if (!isReview && !isInvalidEdited) return;
        setSubmittingIndex(rowIndex);
        setSubmitErrorByRow(prev => ({ ...prev, [rowIndex]: null }));
        try {
            const params = {
                postedOn: row.postedOn || '',
                description: row.description || '',
                amount: parseFloat(row.amount) || 0,
                note: row.note || '',
                source: row.source || '',
                type: row.type || '',
                vendor: row.vendor || '',
                category: row.category || '',
                subCategory: row.subCategory || ''
            };
            const res = await transactionsApi.createSingle(params);
            if (res?.msg === 'Success') {
                setRows(prev => {
                    const next = [...prev];
                    next[rowIndex] = { ...next[rowIndex], status: 'IMPORTED' };
                    return next;
                });
            } else {
                setSubmitErrorByRow(prev => ({ ...prev, [rowIndex]: res?.msg ?? 'Submit failed' }));
            }
        } catch (err) {
            console.error('Submit failed:', err);
            setSubmitErrorByRow(prev => ({ ...prev, [rowIndex]: err?.message ?? 'Submit failed' }));
        } finally {
            setSubmittingIndex(null);
        }
    };

    const canSubmit = (rowIndex) => {
        const row = rows[rowIndex];
        if (row.status === 'DUPLICATE') return false;
        if (row.status === 'REVIEW') return true;
        if (row.status === 'INVALID') return editedRows.has(rowIndex);
        return false;
    };

    const getSubCategoriesForCategory = (category) => {
        if (!category || category === '') {
            return [];
        }
        return subCategoriesMap[category] || [];
    };

    return (
        <AppLayout>
            <div style={{ padding: '20px' }}>
                <h1>Review Upload</h1>
                {rows.length === 0 ? (
                    <p>No rows to review.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '80px' }}>Status</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '110px' }}>Posted On</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', minWidth: '115px' }}>Type</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', minWidth: '220px' }}>Raw Description</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', minWidth: '220px' }}>Description</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Vendor</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '95px' }}>Amount</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', minWidth: '95px' }}>Source</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Category</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Sub Category</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Message</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', minWidth: '140px' }}>Note</th>
                                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index} style={{
                                        backgroundColor: row.status === 'DUPLICATE' ? '#e8e8e8' : row.status === 'INVALID' ? '#ffe6e6' : '#fff',
                                        opacity: row.status === 'DUPLICATE' ? 0.9 : 1
                                    }}>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            <span style={{ 
                                                padding: '4px 8px', 
                                                borderRadius: '4px',
                                                backgroundColor: row.status === 'INVALID' ? '#ff9999' : row.status === 'DUPLICATE' ? '#b0b0b0' : '#99ff99',
                                                color: '#000'
                                            }}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '110px' }}>
                                            <input
                                                type="date"
                                                value={row.postedOn || ''}
                                                onChange={(e) => handleFieldChange(index, 'postedOn', e.target.value)}
                                                style={{ width: '100%', padding: '5px' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '115px' }}>
                                            <select
                                                value={row.type || ''}
                                                onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                                                style={{ width: '100%', minWidth: '100px', padding: '5px' }}
                                            >
                                                <option value="">Select type</option>
                                                <option value="WITHDRAWL">WITHDRAWL</option>
                                                <option value="DEPOSIT">DEPOSIT</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '220px' }}>
                                            <input
                                                type="text"
                                                value={row.rawDesc || ''}
                                                readOnly
                                                style={{ width: '100%', minWidth: '200px', padding: '5px', boxSizing: 'border-box' }}
                                                title="Raw description (read-only)"
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '220px' }}>
                                            <input
                                                type="text"
                                                value={row.description || ''}
                                                onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                                                placeholder="Description"
                                                style={{ width: '100%', minWidth: '200px', padding: '5px', boxSizing: 'border-box' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            <input
                                                type="text"
                                                value={row.vendor || ''}
                                                onChange={(e) => handleFieldChange(index, 'vendor', e.target.value)}
                                                style={{ width: '100%', padding: '5px' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', width: '95px', maxWidth: '95px' }}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={row.amount || ''}
                                                onChange={(e) => handleFieldChange(index, 'amount', parseFloat(e.target.value) || 0)}
                                                style={{ width: '100%', maxWidth: '85px', padding: '5px', boxSizing: 'border-box' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '95px' }}>
                                            <select
                                                value={row.source || ''}
                                                onChange={(e) => handleFieldChange(index, 'source', e.target.value)}
                                                style={{ width: '100%', padding: '5px' }}
                                            >
                                                <option value="">Select source</option>
                                                <option value="SCHWAB">SCHWAB</option>
                                                <option value="CHASE">CHASE</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            <select
                                                value={row.category || ''}
                                                onChange={(e) => handleFieldChange(index, 'category', e.target.value)}
                                                style={{ width: '100%', padding: '5px' }}
                                            >
                                                <option value="">NONE</option>
                                                {categories.map((cat) => (
                                                    <option key={cat} value={cat}>
                                                        {cat}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            <select
                                                value={row.subCategory || ''}
                                                onChange={(e) => handleFieldChange(index, 'subCategory', e.target.value)}
                                                style={{ width: '100%', padding: '5px' }}
                                                disabled={!row.category || row.category === ''}
                                            >
                                                <option value="">NONE</option>
                                                {getSubCategoriesForCategory(row.category).map((subCat) => (
                                                    <option key={subCat} value={subCat}>
                                                        {subCat}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            {row.msg || '-'}
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '140px' }}>
                                            <input
                                                type="text"
                                                value={row.note || ''}
                                                onChange={(e) => handleFieldChange(index, 'note', e.target.value)}
                                                placeholder="Note"
                                                style={{ width: '100%', minWidth: '120px', padding: '5px', boxSizing: 'border-box' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                            {submitErrorByRow[index] && (
                                                <div style={{ color: '#c00', fontSize: '12px', marginBottom: '4px' }}>{submitErrorByRow[index]}</div>
                                            )}
                                            <button
                                                onClick={() => handleSubmit(index)}
                                                disabled={!canSubmit(index) || submittingIndex === index}
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: canSubmit(index) && submittingIndex !== index ? '#007bff' : '#ccc',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: canSubmit(index) && submittingIndex !== index ? 'pointer' : 'not-allowed'
                                                }}
                                            >
                                                {submittingIndex === index ? 'Submitting...' : 'Submit'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

export { ReviewUpload };
